import { useSubscription } from '@apollo/client';
import React from 'react';
import { useContext, useMemo, useState } from 'react';
import { CompanyContext } from '../context/CompanyContext';
import { MobileStockLocationSessionContext } from '../context/MobileStockLocationSessionContext';
import { StockLocationContext } from '../context/StockLocationContext';
import {
  GetMobileStockLocationSessionsResponse,
  GetMobileStockLocationSessionsVariables,
  MobileStockLocationQueries,
  MobileStockLocationSessionNotificationResponse,
  MobileStockLocationSessionNotificationVariables,
  MobileStockLocationSubscriptions,
} from '../graphql/mobileStockLocation.graphql';
import useQuery from '../hooks/useQuery';
import { MobileStockLocationSession } from '../types/mobile_stock_location';
import { toGroupedMap } from '../util/map.util';

interface MobileStockLocationSessionProviderProps {
  children: React.ReactNode;
  stockLocationIds?: Set<string>;
}

export default function MobileStockLocationSessionProvider({
  children,
  stockLocationIds,
}: MobileStockLocationSessionProviderProps) {
  const { currentCompany } = useContext(CompanyContext);
  const { stockLocations, stockLocationsLoading } = useContext(StockLocationContext);

  stockLocationIds ??= new Set([...stockLocations.keys()]);

  const [sessions, setSessions] = useState(new Map<string, MobileStockLocationSession>());

  const sessionsByStockLocationId = useMemo(() => {
    return toGroupedMap([...sessions.values()], 'stockLocationId');
  }, [sessions]);

  const { loading } = useQuery<GetMobileStockLocationSessionsResponse, GetMobileStockLocationSessionsVariables>(
    MobileStockLocationQueries.get,
    {
      variables: { page: 0 },
      onCompleted: response => {
        handleSessions(response.mobileStockLocationSessions.data);
      },
    },
  );

  useSubscription<MobileStockLocationSessionNotificationResponse, MobileStockLocationSessionNotificationVariables>(
    MobileStockLocationSubscriptions.sessions,
    {
      variables: { companyId: currentCompany.id, stockLocationIds: [...stockLocationIds] },
      onSubscriptionData(options) {
        const data = options.subscriptionData.data;
        if (!data) return;
        const entities = data.mobileStockLocationSessionNotification.entities;
        switch (data.mobileStockLocationSessionNotification.topic) {
          case 'CREATE':
          case 'UPDATE':
            return handleSessions(entities);
          case 'DELETE':
            return handleRemoveSessions(entities);
        }
      },
    },
  );

  const handleSessions = (s: MobileStockLocationSession[]) => {
    s.forEach(session => {
      sessions.set(session.id, new MobileStockLocationSession(session));
    });
    setSessions(new Map(sessions));
  };

  const handleRemoveSessions = (s: MobileStockLocationSession[]) => {
    s.forEach(session => {
      sessions.delete(session.id);
    });
    setSessions(new Map(sessions));
  };

  const context = {
    sessions,
    sessionsLoading: loading,
    sessionsByStockLocationId: sessionsByStockLocationId,
  };

  if (stockLocationsLoading || loading) return null;

  return (
    <MobileStockLocationSessionContext.Provider value={context}>{children}</MobileStockLocationSessionContext.Provider>
  );
}
